import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Toaster } from "../components/ui/toaster";
import { useToast } from "../hooks/use-toast";
import { ArrowRight, Sprout, Crown, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const SignupPage = () => {
  const { register, createChurch, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stage, setStage] = useState(user ? "church" : "account");
  const [submitting, setSubmitting] = useState(false);

  const [acct, setAcct] = useState({ name: "", email: "", password: "" });
  const [church, setChurch] = useState({
    name: "",
    slug: "",
    description: "",
    accent_color: "#14b8a6",
    welcome_message: "",
  });

  const submitAccount = async (e) => {
    e.preventDefault();
    if (acct.name.length < 2 || !acct.email.includes("@") || acct.password.length < 8) {
      toast({
        title: "Check your details",
        description: "Name 2+ chars, valid email, password 8+ chars.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await register(acct);
      setStage("church");
    } catch (err) {
      toast({
        title: "Could not create account",
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitChurch = async (e) => {
    e.preventDefault();
    if (church.name.length < 2 || church.slug.length < 3) {
      toast({
        title: "Check church details",
        description: "Name and URL slug are required.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const c = await createChurch(church);
      toast({
        title: "Your hub is live",
        description: `Visit it anytime at /c/${c.slug}`,
      });
      navigate("/dashboard");
    } catch (err) {
      toast({
        title: "Could not create church",
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const slugify = (txt) =>
    txt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 40);

  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />
      <section className="pt-28 md:pt-36 pb-16 bg-[#04181a]">
        <div className="max-w-2xl mx-auto px-6">
          {/* Stepper */}
          <div className="flex items-center gap-3 mb-8 text-xs uppercase tracking-wider">
            <div className={`flex items-center gap-2 ${stage === "account" ? "text-teal-300" : "text-white/40"}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center border ${stage === "account" ? "border-teal-400 bg-teal-400/20" : "border-white/20 bg-white/5"}`}>
                {stage !== "account" ? <Check className="w-3 h-3 text-teal-300" /> : "1"}
              </span>
              Create account
            </div>
            <div className="h-px flex-1 bg-white/10" />
            <div className={`flex items-center gap-2 ${stage === "church" ? "text-teal-300" : "text-white/40"}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center border ${stage === "church" ? "border-teal-400 bg-teal-400/20" : "border-white/20 bg-white/5"}`}>
                2
              </span>
              Plant your hub
            </div>
          </div>

          {stage === "account" ? (
            <>
              <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 text-teal-200 px-3 py-1.5 rounded-full text-xs">
                <Sprout className="w-3.5 h-3.5" /> Start free
              </div>
              <h1 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight">
                Create your{" "}
                <em className="text-teal-300 font-serif font-normal">Good Fruit account.</em>
              </h1>
              <form onSubmit={submitAccount} className="mt-8 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/85 mb-2">Your name</label>
                  <Input
                    value={acct.name}
                    onChange={(e) => setAcct((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Min. Tyree Huddleston"
                    className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400 focus-visible:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/85 mb-2">Email</label>
                  <Input
                    type="email"
                    value={acct.email}
                    onChange={(e) => setAcct((f) => ({ ...f, email: e.target.value }))}
                    placeholder="you@church.org"
                    className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400 focus-visible:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/85 mb-2">Password</label>
                  <Input
                    type="password"
                    value={acct.password}
                    onChange={(e) => setAcct((f) => ({ ...f, password: e.target.value }))}
                    placeholder="At least 8 characters"
                    className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400 focus-visible:border-teal-400"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12 disabled:opacity-60"
                >
                  {submitting ? "Creating…" : "Continue"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
              <div className="mt-6 text-sm text-white/55 text-center">
                Already have an account?{" "}
                <Link to="/login" className="text-teal-300 hover:text-teal-200 underline underline-offset-2">
                  Sign in
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 text-teal-200 px-3 py-1.5 rounded-full text-xs">
                <Crown className="w-3.5 h-3.5" /> Plant your hub
              </div>
              <h1 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight">
                Set up your{" "}
                <em className="text-teal-300 font-serif font-normal">church hub.</em>
              </h1>
              <p className="mt-3 text-white/60 text-sm">
                You can change any of this later from your dashboard.
              </p>
              <form onSubmit={submitChurch} className="mt-8 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/85 mb-2">Church name</label>
                  <Input
                    value={church.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setChurch((c) => ({
                        ...c,
                        name,
                        slug: c.slug || slugify(name),
                      }));
                    }}
                    placeholder="Hope Chapel"
                    className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400 focus-visible:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/85 mb-2">Hub URL</label>
                  <div className="flex items-center gap-2">
                    <span className="text-white/45 text-sm whitespace-nowrap">goodfruit.app/c/</span>
                    <Input
                      value={church.slug}
                      onChange={(e) =>
                        setChurch((c) => ({ ...c, slug: slugify(e.target.value) }))
                      }
                      placeholder="hope-chapel"
                      className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400 focus-visible:border-teal-400"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/85 mb-2">Short description</label>
                  <Textarea
                    value={church.description}
                    onChange={(e) =>
                      setChurch((c) => ({ ...c, description: e.target.value }))
                    }
                    placeholder="What is your community about?"
                    rows={2}
                    className="bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400 focus-visible:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/85 mb-2">Welcome message for members</label>
                  <Textarea
                    value={church.welcome_message}
                    onChange={(e) =>
                      setChurch((c) => ({ ...c, welcome_message: e.target.value }))
                    }
                    placeholder="A few warm words members see when they visit…"
                    rows={3}
                    className="bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400 focus-visible:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/85 mb-2">Accent color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={church.accent_color}
                      onChange={(e) =>
                        setChurch((c) => ({ ...c, accent_color: e.target.value }))
                      }
                      className="h-11 w-14 rounded-lg border border-white/15 bg-transparent cursor-pointer"
                    />
                    <code className="text-sm text-white/60 font-mono">{church.accent_color}</code>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12 disabled:opacity-60"
                >
                  {submitting ? "Planting…" : "Plant my hub"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </>
          )}
        </div>
      </section>
      <Footer />
      <Toaster />
    </div>
  );
};

export default SignupPage;
